import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { Observable, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { v4 as newGuid } from 'uuid';

import {
  BaseEditCreateService,
  BaseEditService,
  FormService,
  ModalService,
  NotificationService,
  RouterService,
} from '../../services';
import { MockEditService } from '../../mock';
import { BaseComponent } from '../base/base.component';

@Component({
  selector: 'app-base-edit-form',
  templateUrl: './base-edit-form.component.html',
  styleUrl: './base-edit-form.component.scss',
})
export class BaseEditFormComponent extends BaseComponent implements OnInit, OnDestroy {
  @Input() fields: FormlyFieldConfig[];
  @Input() baseService: BaseEditService<any, any> | BaseEditCreateService<any, any> = new MockEditService();
  @Input() updateModelOnSave = false;
  @Input() closeOnSave = true;
  @Input() closeOnCreate = false;
  @Input() alwaysSave = false;
  @Input() id: string;
  @Input() parentId: string;
  @Input() parentId2: string;
  @Input() model: any = {};
  @Output() modelLoaded = new EventEmitter<any>();
  @Output() modelUpdated = new EventEmitter<any>();
  @Output() saved = new EventEmitter<any>();

  form: UntypedFormGroup = new UntypedFormGroup({});
  options: FormlyFormOptions = {};

  private modalLevel: number;
  private formId: string;

  constructor(
    private formService: FormService,
    private notificationService: NotificationService,
    private routerService: RouterService,
    private route: ActivatedRoute,
    private modalService: ModalService,
  ) {
    super();
  }

  ngOnInit() {
    this.modalLevel = this.modalService.modalLevel;
    this.getModel();
    this.formService.save.pipe(takeUntil(this.destroy$)).subscribe((modalLevel: number) => {
      if (modalLevel === this.modalLevel) {
        this.onSave();
      }
    });
    this.formId = newGuid();
    this.formService.addForm(this.modalLevel, this.formId, this.form);
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.formService.removeForm(this.modalLevel, this.formId);
  }

  get isModal() {
    return this.modalLevel > 0;
  }

  get isDirty() {
    return this.form?.dirty ?? false;
  }

  get observableModel(): Observable<any> {
    return of(this.model);
  }

  getModel() {
    const id = this.isModal ? this.id : this.route.snapshot.paramMap.get('id');
    (id ? this.baseService.edit(id) : this.baseService.create(this.parentId, this.parentId2))
      .pipe(takeUntil(this.destroy$))
      .subscribe((model: any) => {
        this.updateModel(model, false);
        this.modelLoaded.emit(model);
      });
  }

  onSave() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.formService.scrollToFirstInvalidField();
      return;
    }

    if (!this.form.dirty && !this.alwaysSave) {
      setTimeout(() => {
        if (!this.formService.anySavesInProgress) {
          this.navigate(false);
        }
      }, 0);
      return;
    }

    this.formService.beginSave();
    this.baseService
      .save(this.model)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (id: string) => {
          this.notificationService.saveSuccess(this.baseService.name);

          if (this.updateModelOnSave) {
            this.getModel();
          }

          this.formService.saveComplete.pipe(takeUntil(this.destroy$)).subscribe(() => this.navigate(true, id));

          this.formService.endSave(false);
        },
        error: () => {
          this.notificationService.saveFail(this.baseService.name);
          this.formService.endSave(true);
        },
      });
  }

  private navigate(saved: boolean, id?: string) {
    if (this.isModal) {
      if (this.closeOnSave) {
        this.modalService.close(saved ? this.model : undefined);
      }
    } else {
      if (this.closeOnSave) {
        this.routerService.back(this.route);
      } else {
        if (this.formService.isNewModel(this.model) && !this.closeOnCreate) {
          if (id) {
            this.routerService.edit(id, this.route);
          }
        }
      }
    }
    if (id) {
      this.model = { ...this.model, id };
    }
    this.options.resetModel();
    this.formService.resetForms(this.modalLevel);
    if (saved) {
      this.saved.emit(this.model);
    }
  }

  updateModel(model: any, markUpdated = true, markUnchanged = false) {
    this.model = { ...this.model, ...model };
    this.modelUpdated.emit(this.model);
    if (markUpdated) {
      this.form.markAsDirty();
      this.form.markAllAsTouched();
    }
    if (markUnchanged) {
      this.form.markAsPristine();
      this.form.markAsUntouched();
    }
  }
}
